// Impresión / "Guardar como PDF" del organigrama — SIN dependencias.
// Arma un HTML autocontenido con el árbol indentado y lo manda a imprimir con el print()
// nativo del navegador dentro de un iframe oculto. El diálogo del navegador permite
// imprimir físico o "Guardar como PDF"; la paginación (multipágina) la hace el navegador.

// Colores de acento por CÓDIGO de tipo (mismos que las cards del organigrama en pantalla;
// si se cambia uno, cambiar también .card_* en Organigrama.module.scss). "DIR" es la raíz
// sintética del front.
const COLORES = {
  DIR: "#334155", D: "#3b82f6", I: "#8b5cf6", U: "#10b981",
  C: "#0891b2", P: "#ca8a04", O: "#db2777", R: "#9333ea",
};

// Leyenda: [código de tipo, etiqueta].
const LEYENDA = [
  ["D", "Departamento"], ["I", "División"], ["U", "Unidad"],
  ["C", "CAPS"], ["P", "Puesto"], ["O", "Posta"], ["R", "URESA"],
];

// Escapa texto que va al HTML (los nombres vienen del back).
function escapeHtml(str) {
  return String(str ?? "").replace(/[&<>"']/g, (c) => (
    { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]
  ));
}

// CSS del documento a imprimir. `print-color-adjust: exact` fuerza a que los colores
// (acentos y leyenda) se impriman; `break-inside: avoid` evita cortar una card entre hojas.
const CSS = `
* { box-sizing: border-box; }
html, body { margin: 0; padding: 0; }
body {
  font-family: -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  color: #111827;
  padding: 6px 8px;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}
@page { margin: 12mm; }
h1 { margin: 0; font-size: 22px; font-weight: 800; }
.sub { margin-top: 4px; font-size: 13px; color: #6b7280; }
.hdr { border-bottom: 2px solid #e5e7eb; padding-bottom: 10px; margin-bottom: 16px; }
.leg { display: flex; flex-wrap: wrap; gap: 6px 16px; margin-top: 10px; font-size: 11px; color: #374151; }
.leg-item { display: inline-flex; align-items: center; gap: 6px; }
.leg-color { width: 11px; height: 11px; border-radius: 2px; display: inline-block; }
ul.raiz, ul.hijos { list-style: none; margin: 0; padding: 0; }
ul.hijos { margin-left: 28px; }
li.item { position: relative; }
ul.hijos > li.item { padding-left: 20px; }
ul.hijos > li.item::before { content: ""; position: absolute; left: 0; top: 0; bottom: 0; border-left: 1.5px solid #cbd5e1; }
ul.hijos > li.item:last-child::before { bottom: auto; height: 18px; }
ul.hijos > li.item::after { content: ""; position: absolute; left: 0; top: 18px; width: 20px; border-top: 1.5px solid #cbd5e1; }
.card {
  display: inline-block;
  border: 1px solid #e5e7eb;
  border-left: 4px solid #cbd5e1;
  border-radius: 6px;
  padding: 5px 10px;
  margin: 3px 0;
  font-size: 12.5px;
  font-weight: 600;
  max-width: 360px;
  overflow-wrap: anywhere;
  break-inside: avoid;
}
.ftr { margin-top: 18px; font-size: 10px; color: #9ca3af; }
`;

// Rama recursiva: card del nodo + <ul> de hijos (si tiene). La raíz no lleva guía (va en
// <ul class="raiz">); los hijos sí (<ul class="hijos">).
function ramaHtml(nodo, hijosPorId) {
  const color = COLORES[nodo.rnhTipoEstructuraCod] || "#cbd5e1";
  const hijos = hijosPorId.get(nodo.rnhEstablecimientoEstructuraId) ?? [];
  const card = `<div class="card" style="border-left-color:${color}">${escapeHtml(nodo.rnhEstructuraNombre)}</div>`;
  const sub = hijos.length
    ? `<ul class="hijos">${hijos.map((h) => ramaHtml(h, hijosPorId)).join("")}</ul>`
    : "";
  return `<li class="item">${card}${sub}</li>`;
}

// Documento HTML completo (encabezado + leyenda + árbol).
function construirHtml(roots, hijosPorId, hospitalInfo) {
  const titulo = escapeHtml(hospitalInfo?.rnhEstablecimientoNombre || "Organigrama");
  // OJO CON EL PREFIJO: en la fila del establecimiento el nivel, la zona y el area vienen
  // desnormalizados como `rnhEstablecimiento*`. Esto leia `rnhNivelCod`, `rnhZonaNombre` y
  // `rnhAreaNombre`, que no existen ahi, asi que los tres daban undefined y el organigrama
  // impreso salia con el titulo del hospital y el subtitulo vacio.
  //
  // `nivelNombre` no es de la response: lo resuelve la vista contra el catalogo de niveles,
  // porque el nombre del nivel es lo unico de los tres que el establecimiento no trae.
  const sub = [
    hospitalInfo?.nivelNombre || hospitalInfo?.rnhEstablecimientoNivelCod,
    hospitalInfo?.rnhEstablecimientoZonaNombre,
    hospitalInfo?.rnhEstablecimientoAreaNombre,
  ].filter(Boolean).map(escapeHtml).join("   ·   ");
  const fecha = new Date().toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric" });
  const leyenda = LEYENDA
    .map(([t, label]) => `<span class="leg-item"><span class="leg-color" style="background:${COLORES[t]}"></span>${label}</span>`)
    .join("");
  const arbol = `<ul class="raiz">${(roots ?? []).map((r) => ramaHtml(r, hijosPorId)).join("")}</ul>`;

  return `<!doctype html><html lang="es"><head><meta charset="utf-8"><title>${titulo}</title><style>${CSS}</style></head>` +
    `<body>` +
    `<header class="hdr"><h1>${titulo}</h1>${sub ? `<div class="sub">${sub}</div>` : ""}<div class="leg">${leyenda}</div></header>` +
    `${arbol}` +
    `<footer class="ftr">Generado el ${fecha}</footer>` +
    `</body></html>`;
}

/**
 * Abre el diálogo de impresión del navegador con el organigrama (imprimir físico o
 * "Guardar como PDF"). Usa un iframe oculto para no chocar con bloqueadores de popups.
 */
export function imprimirArbol(roots, hijosPorId, hospitalInfo) {
  const iframe = document.createElement("iframe");
  Object.assign(iframe.style, {
    position: "fixed", right: "0", bottom: "0", width: "0", height: "0", border: "0",
  });
  document.body.appendChild(iframe);

  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(construirHtml(roots, hijosPorId, hospitalInfo));
  doc.close();

  const limpiar = () => { if (document.body.contains(iframe)) iframe.remove(); };
  const lanzar = () => {
    iframe.contentWindow.focus();
    iframe.contentWindow.onafterprint = limpiar; // se quita al cerrar el diálogo
    iframe.contentWindow.print();
    setTimeout(limpiar, 60000); // fallback por si onafterprint no dispara
  };
  // Contenido inline (sin recursos externos): con un frame alcanza para que quede maquetado.
  setTimeout(lanzar, 150);
}
